import { DebugKey, canUseDOM, createDebug } from '@ps/web-core-utils';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { MemoryRouter } from 'react-router';
import { State as XState, interpret } from 'xstate';

import type {
  FlowProp,
  MachineOptions,
  State,
  Wizard,
  WizardContext
} from '@swf/core/types/Wizard';

import { Node } from './Flow';
import {
  createConfig,
  create as createMachine,
  defaultConfig as defaultMachineConfig,
  getMeta
} from './Machine';
import { Context as ServerContext } from './Server';
import Group from './components/Group/Group';
import Navigation, { getInitial } from './components/Navigation/Navigation';
import Page from './components/Page/Page';

const wizardDebug = createDebug(DebugKey.Wizard);
const { context, id: wizardName } = defaultMachineConfig();

type Props = FlowProp &
  MachineOptions & { cmsHeaderAndGridLayout?: boolean; loader?: React.ReactNode };

interface WizardComponent extends React.FunctionComponent<React.PropsWithChildren<Props>> {
  Page: typeof Page;
  Group: typeof Group;
}

export const Context = React.createContext<Wizard | null>(null);
export const DataContext = React.createContext<WizardContext>(context);
export const FlowContext = React.createContext<Node | null>(null);

const StarsWizard: WizardComponent = ({
  children,
  cmsHeaderAndGridLayout,
  flow,
  loader,
  ...machineOptions
}) => {
  const [wizard, setWizard] = useState<Wizard>();
  const [node] = useState<Node>(new Node(wizardName, flow, machineOptions));
  const [state, setState] = useState<State>();
  const [showLoader, setShowLaoder] = useState(!!loader);
  const serverContext = useContext(ServerContext);
  const serverState = serverContext.state;
  const externalState = canUseDOM && window.__WIZARD_STATE__;
  const transitionHandler = useCallback((value: State) => {
    wizardDebug('state %o', value);
    setState(value);
    if (
      showLoader &&
      value.value !== 'idle' &&
      (getMeta(value.meta) as Record<string, boolean>).navigate
    ) {
      setShowLaoder(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  serverContext.node = node;

  useEffect(() => {
    const machine = createMachine(createConfig(node, cmsHeaderAndGridLayout), node.options);
    const interpreter = interpret(machine);

    interpreter.onTransition(transitionHandler);

    if (externalState) {
      interpreter.start(machine.resolveState(XState.create(externalState)));
      delete window.__WIZARD_STATE__;
    } else {
      interpreter.start();
    }

    setWizard(interpreter);

    return () => {
      interpreter.off(transitionHandler);
    };
  }, [node, externalState, cmsHeaderAndGridLayout, transitionHandler]);

  return (
    <MemoryRouter
      keyLength={process.env.NODE_ENV === 'test' ? 0 : 6}
      {...getInitial(externalState ? externalState : serverState)}
    >
      <FlowContext.Provider value={node}>
        <DataContext.Provider
          value={[state as State, serverState as State].reduce(
            (result: WizardContext, value: State) => (value ? value.context : result),
            context
          )}
        >
          <Navigation wizard={wizard} state={serverState} bootstrap={!externalState}>
            <Context.Provider value={wizard || null}>
              <>
                {showLoader && loader}
                {children}
              </>
            </Context.Provider>
          </Navigation>
        </DataContext.Provider>
      </FlowContext.Provider>
    </MemoryRouter>
  );
};

StarsWizard.Group = Group;
StarsWizard.Page = Page;

export default StarsWizard;
